import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Heart, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const MAX_ATTEMPTS = 6;
const INTERVAL = 2000;

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [info, setInfo] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        setInfo(data);
        if (data.status === "paid") { setState("success"); return; }
        if (data.status === "expired") { setState("failed"); return; }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) { setState("timeout"); return; }
      setTimeout(poll, INTERVAL);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  const amount = info?.amount ? `$${Number(info.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "";

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-4 py-20 text-center" data-testid="donation-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <h1 className="mt-6 text-2xl font-[720] text-foreground">Confirming your contribution…</h1>
          <p className="mt-2 text-muted-foreground">Hang tight, this only takes a moment.</p>
        </>
      )}

      {state === "success" && (
        <div data-testid="donation-success-confirmed">
          <Heart className="mx-auto h-14 w-14 text-primary" />
          <h1 className="mt-6 text-3xl font-[720] text-foreground">Thank you! 🦦</h1>
          <p className="mt-3 text-muted-foreground">
            Your {amount && <span className="font-[640] text-foreground">{amount}</span>}
            {info?.frequency === "monthly" ? " monthly " : " "}contribution came through.
            We&apos;ve emailed you a thank-you note; Stripe sends the receipt separately.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Support like yours keeps Startup Otter running for founders, businesses and investors.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/chatter"><Button data-testid="donation-success-continue">Back to Startup Otter</Button></Link>
            <Link to="/pricing"><Button variant="secondary">See memberships</Button></Link>
          </div>
        </div>
      )}

      {(state === "failed" || state === "timeout") && (
        <div data-testid="donation-success-failed">
          <XCircle className="mx-auto h-14 w-14 text-muted-foreground" />
          <h1 className="mt-6 text-2xl font-[720] text-foreground">
            {state === "timeout" ? "Still processing" : "Contribution not completed"}
          </h1>
          <p className="mt-3 text-muted-foreground">
            {state === "timeout"
              ? "Your payment is taking longer than expected. If you completed checkout, it will land shortly and you'll get an email."
              : "We couldn't confirm a completed payment. If you were charged, please contact support."}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/pricing"><Button>Back to pricing</Button></Link>
            <Link to="/contact"><Button variant="secondary">Contact support</Button></Link>
          </div>
        </div>
      )}
    </div>
  );
}
